import { calculateClockDuration, calculateDatedDuration, getEntryDuration } from './tradeDuration';

const UNIT_MINUTES = [
  [/^(mo|mos|month|months)$/, 30 * 1440],
  [/^(d|day|days)$/, 1440],
  [/^(h|hr|hrs|hour|hours)$/, 60],
  [/^(m|min|mins|minute|minutes)$/, 1],
];

/** Minutes in a duration such as "1hr 5min", "60m", "2d 1hr" or "65 min"; null when it can't be read. */
export function parseDurationMinutes(value) {
  const compact = String(value || '').toLowerCase().replace(/[\s,]+/g, '');
  if (!/^(\d+(?:\.\d+)?[a-z]+)+$/.test(compact)) return null;
  let total = 0;
  for (const [, amount, unitText] of compact.matchAll(/(\d+(?:\.\d+)?)([a-z]+)/g)) {
    const unit = UNIT_MINUTES.find(([pattern]) => pattern.test(unitText));
    if (!unit) return null;
    total += Number(amount) * unit[1];
  }
  return Math.round(total);
}

/** Same length of time, even when written differently ("60m" and "1hr"). */
export function isSameDuration(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (left === right) return true;
  const leftMinutes = parseDurationMinutes(left);
  return leftMinutes !== null && leftMinutes === parseDurationMinutes(right);
}

/**
 * Duration the entry form would fill in from its own date/time inputs.
 * Returns null while the inputs are incomplete (leave the duration alone),
 * otherwise the calculated value ('' when the times cannot be used).
 */
export function getFormAutoDuration({ tradingStyle, entryDate, exitDate, entryTime, exitTime }) {
  if (!entryTime || !exitTime) return null;
  if (tradingStyle === 'swing') {
    if (!entryDate || !exitDate) return null;
    return calculateDatedDuration(entryDate, entryTime, exitDate, exitTime);
  }
  return calculateClockDuration(entryTime, exitTime);
}

/**
 * Duration to show when an existing entry is opened for editing, and whether it
 * should be treated as the user's own value (never overwritten automatically).
 * Entries saved without a duration get the one the rest of the app already shows.
 */
export function getEditableDurationFromEntry(entry = {}) {
  const savedDuration = String(entry.duration || '').trim();
  const duration = getEntryDuration(entry) || savedDuration;
  if (!savedDuration) return { duration, edited: false };

  const entryDate = entry.tradingStyle === 'swing'
    ? [entry.entryYear || entry.year, entry.entryMonth || entry.month, entry.entryDay || entry.day]
    : [];
  const exitDate = [entry.exitYear, entry.exitMonth, entry.exitDay];
  const toDate = (parts) => (parts.length === 3 && parts.every(Boolean)
    ? `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`
    : '');
  const autoDuration = getFormAutoDuration({
    tradingStyle: entry.tradingStyle,
    entryDate: toDate(entryDate),
    exitDate: toDate(exitDate),
    entryTime: entry.entryTime,
    exitTime: entry.exitTime,
  });
  return { duration, edited: !isSameDuration(duration, autoDuration) };
}

/** The "Use X" suggestion for the duration field, or '' when there is nothing to suggest. */
export function getDurationSuggestion(currentDuration, autoDuration) {
  if (!autoDuration) return '';
  return isSameDuration(currentDuration, autoDuration) ? '' : autoDuration;
}
